import { DEFAULT_LOCALE, findLocale, findLocaleBySlug } from './locales'

/**
 * Locale-prefixed URLs.
 *
 * The default locale lives at the bare path and every other locale sits under its
 * slug as the FIRST path segment:
 *
 *   /subnet-calculator/          -> en
 *   /pt-br/subnet-calculator/    -> pt-BR
 *   /es/subnet-calculator/       -> es
 *
 * Putting the locale first (rather than inside the tool's path) keeps it
 * independent of Vite's `base`, so a tool can move to its own subdirectory without
 * touching any of this.
 *
 * These are pure string functions — no `window` — so they are unit-testable and
 * usable from the build plugin.
 */

/** The locale named by the first path segment, or `null` when none is. */
export function localeFromPath(pathname: string): string | null {
  const [first] = pathname.replace(/^\/+/, '').split('/')
  return findLocaleBySlug(first ?? '')?.code ?? null
}

/** The path with any locale prefix removed, always leading with a slash. */
export function stripLocale(pathname: string): string {
  const segments = pathname.replace(/^\/+/, '').split('/')
  if (findLocaleBySlug(segments[0] ?? '')) segments.shift()
  return `/${segments.join('/')}`
}

/** The same page in another locale. Idempotent, and safe on already-prefixed paths. */
export function localizedPath(pathname: string, locale: string): string {
  const bare = stripLocale(pathname)
  const slug = findLocale(locale)?.slug ?? ''
  if (slug === '') return bare
  return `/${slug}${bare === '/' ? '/' : bare}`
}

/** Absolute URL for a locale, used for canonical and hreflang tags. */
export function localizedUrl(siteUrl: string, toolPath: string, locale: string): string {
  const origin = siteUrl.replace(/\/+$/, '')
  const path = localizedPath(toolPath.startsWith('/') ? toolPath : `/${toolPath}`, locale)
  return `${origin}${path}`
}

/**
 * Where a locale's HTML file belongs in the build output, relative to the site root.
 * The default locale keeps the plain `index.html`.
 */
export function localePageFile(toolPath: string, locale: string): string {
  const path = localizedPath(toolPath.startsWith('/') ? toolPath : `/${toolPath}`, locale)
  const trimmed = path.replace(/^\/+/, '').replace(/\/+$/, '')
  return trimmed === '' ? 'index.html' : `${trimmed}/index.html`
}

export { DEFAULT_LOCALE }
